import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import '../patientForm.css';
import { Formik, Form, useFormik, ErrorMessage, Field } from 'formik';
import { Loader, Select } from '@ohif/ui';
import { PatientPropsType } from '@custom/Utils/constants/interfaces';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _patientUrls } from '@custom/Utils/constants/apiUrls';
import { servicesManager } from '../../../app/src/App';
import { useNavigate, useLocation } from 'react-router-dom';
import { EmergencyInfoAndReferringFormSchema } from './schema';
import { customStyle, getCurrencyContact } from '@custom/Utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';

const EmergencyContactandPhysician = (props: PatientPropsType) => {
  const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);

  const [countryCode, setCountryCode] = useState({
    physicianWhatsappNumberCountryCode: "+91",
    physicianContactNumberCountryCode: "+91",

  });
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('+91');
  const [sameAsPhoneNo, setSameAsPhoneNo] = useState(false)
  const [load, setLoad] = useState(false)
  const { uiNotificationService } = servicesManager.services
  const data = useLocation()
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    dispatch(getBillingGeneralSettings());
  }, [])

  useEffect(() => {
    if (billingGeneralSettingsData?.status === 'success' && billingGeneralSettingsData?.result?.currency) {
      const newCountryCode = getCurrencyContact(billingGeneralSettingsData.result.currency);
      setSelectedCountryCode(newCountryCode);
    }
  }, [billingGeneralSettingsData]);

  const handleCountryCodeChange = (e: any) => {
    setCountryCode({ ...countryCode, [e.target.id]: e.target.value });
  };

  const defaultCountryCode = billingGeneralSettingsData?.status && billingGeneralSettingsData?.result?.currency ? getCurrencyContact(billingGeneralSettingsData.result.currency) : '+91';

  const getInitialValues = () => {
    if (data.state.mode == 'edit') {
      const editData = {
        emergencyInfo: data.state?.patientDetails?.patientDetail?.emergencyInfo
          ? data.state.patientDetails.patientDetail.emergencyInfo
          : [{
            contactName: null,
            contactRelation: null,
            contactNo: null,
            emergencyContactPhoneNumberCountryCode: defaultCountryCode,
          }],

        physicianName: data.state?.patientDetails?.patientDetail?.referPhysicianInfo?.[0]?.name || "",
        physicianContactNumberCountryCode: defaultCountryCode,
        physicianContactNumber: data.state?.patientDetails?.patientDetail?.referPhysicianInfo?.[0]?.contactNo || "",
        physicianSpeciality: data.state?.patientDetails?.patientDetail?.referPhysicianInfo?.[0]?.speciality || "",
        physicianWhatsappNumberCountryCode: defaultCountryCode,
        physicianWhatsappNumber: data.state?.patientDetails?.patientDetail?.referPhysicianInfo?.[0]?.whatsappNo || "",
      };

      return editData
    } else if (data.state.mode == 'create') {
      const initialData = {
        emergencyInfo: props.patient?.emergencyInfo ? props.patient.emergencyInfo : [{
          contactName: null,
          contactRelation: null,
          contactNo: null,
          emergencyContactPhoneNumberCountryCode: defaultCountryCode,
        }],
        physicianName: props.patient?.referPhysicianInfo?.[0]?.name || "",
        physicianContactNumberCountryCode: defaultCountryCode,
        physicianContactNumber: props.patient?.referPhysicianInfo?.[0]?.contactNo || "",
        physicianSpeciality: props.patient?.referPhysicianInfo?.[0]?.speciality || "",
        physicianWhatsappNumberCountryCode: defaultCountryCode,
        physicianWhatsappNumber: props.patient?.referPhysicianInfo?.[0]?.whatsappNo || "",
      };

      return initialData
    }
  }

  const initialValues = getInitialValues()

  const handleSave = async (values: any) => {

    let payload = {
      "profileStep": props.PageNumber,
      "_id": props.patientDocID,
      "emergencyInfo": values.emergencyInfo,
      "referPhysicianInfo": [{
        "name": values.physicianName,
        "speciality": values.physicianSpeciality,
        "dialCode": selectedCountryCode,
        "contactNo": String(values.physicianContactNumber),
        "whatsappDialCode": selectedCountryCode,
        "whatsappNo": String(values.physicianWhatsappNumber)
      }]
    }

    setLoad(true)
    const response = await apiRequest({ method: 'POST', endUrl: _patientUrls.add, headerType: 'json', body: payload })
    if (response.status) {
      setLoad(false)
      props.setPageNumber((prev: any) => prev + 1)
    } else if (!response.status) {
      setLoad(false)

      uiNotificationService.show({
        message: response.msg,
        type: 'error',
        title: "Error",
        position: "topRight"
      })
    }
  }

  const handleAddEmergencyInfo = (e: any, values: any, setFieldValue: any) => {
    const newEmergencyInfo = {
      contactName: null,
      contactRelation: null,
      contactNo: null,
    };

    setFieldValue('emergencyInfo', [...values.emergencyInfo, newEmergencyInfo]);
  }

  const handleEmergencyInfoChange = (event: any, setFieldValue: any) => {
    const { name, value, dataset } = event.target;
    const index = dataset.index; // Read index from data-index attribute

    if (index !== undefined) {
      // Construct the field path based on index and name
      const fieldPath = `emergencyInfo[${index}].${name}`;
      setFieldValue(fieldPath, value);
    }
  };


  const handleRemoveEmergencyInfo = (index: number, values: any, setFieldValue: any) => {
    const updatedEmergencyInfo = values.emergencyInfo.filter((_: any, i: number) => i !== index);
    setFieldValue('emergencyInfo', updatedEmergencyInfo);
  }

  return (
    <section className='form__section'>
      <Formik
        initialValues={initialValues}
        validationSchema={EmergencyInfoAndReferringFormSchema}
        onSubmit={handleSave}
      >
        {({
          errors,
          values,
          handleChange,
          handleBlur,
          touched,
          setFieldValue,
          setFieldTouched,
        }) => {
          useEffect(() => {
            if (data?.state?.mode !== 'edit') {
              values.emergencyInfo.forEach((_, index) => {
                setFieldValue(`emergencyInfo[${index}].emergencyContactPhoneNumberCountryCode`, selectedCountryCode);
              });
              setFieldValue('emergencyContactPhoneNumberCountryCode', selectedCountryCode);
              setFieldValue('physicianContactNumberCountryCode', selectedCountryCode);
              setFieldValue('physicianWhatsappNumberCountryCode', selectedCountryCode);
            }
          }, [selectedCountryCode, data?.state?.mode]);
          return (
          <Form >
            <h1 className='form__section__title' style={{ justifyContent: "start" }}>Emergency Contact Information <button onClick={(e) => { handleAddEmergencyInfo(e, values, setFieldValue) }} type='button' className='text-lg text-[#51A196] ml-2'>+ Add more</button></h1>
            {
              values.emergencyInfo.map((emergencyDetails, index) => {
                return <>
                  <div className='formRow formRow__3'>
                    <div className="formCol">
                      <div className="form__Field">
                        <div className='flex justify-start items-center gap-4'>
                          <label className='form__label' htmlFor='contactName'>
                            Emergency Contact Name
                          </label>
                          {values.emergencyInfo.length > 1 && <button onClick={(e) => { handleRemoveEmergencyInfo(index, values, setFieldValue) }} type='button' className='text-lg text-[#51A196] mb-1'>Remove</button>}
                        </div>
                        <input
                          type="text"
                          id="contactName"
                          name="contactName"
                          className="form__input form__input--sm"
                          placeholder="Emergency Contact Name"
                          onChange={(e) => handleEmergencyInfoChange(e, setFieldValue)}
                          onBlur={handleBlur}
                          value={emergencyDetails.contactName}
                          data-index={index}
                        />
                      </div>
                    </div>
                    <div className='formCol'>
                      <div className='form__Field'>
                        <label className='form__label' htmlFor='contactRelation' >Emergency Contact Relationship</label>
                        <select
                          id="contactRelation"
                          name="contactRelation"
                          className="form__select form__select--sm"
                          onChange={(e) => handleEmergencyInfoChange(e, setFieldValue)}
                          onBlur={handleBlur}
                          value={emergencyDetails.contactRelation}
                          data-index={index}
                          style={customStyle.select}
                        >
                          <option value="">-Select-</option>
                          {props.commonData?.emergencyRelations.map((option: any) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label className='form__label' htmlFor='name'>
                          Emergency Contact Phone Number
                        </label>
                        <div className='form__groupField form__groupField--phonenumber'>
                          <select id="emergencyContactPhoneNumberCountryCode"
                            style={customStyle.select}
                            onChange={(e) => {
                              setFieldValue(`emergencyContactPhoneNumberCountryCode`, e.target.value);
                            }}
                            className="form__select form__select--sm"
                            value={values.emergencyContactPhoneNumberCountryCode}
                          >
                            {
                              props?.commonData?.countryCode.map((option) => (
                                <option
                                  key={option}
                                  value={option}
                                >
                                  {option}
                                </option>
                              ))
                            }
                          </select>
                          <input
                            className="form__input form__input--sm"
                            type="text"
                            id="contactNo"
                            name="contactNo"
                            pattern="[0-9]*"
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9]/g, '');
                              handleChange(e);
                            }}
                            maxLength={10}
                            placeholder="Emergency Contact Phone Number"
                            onChange={(e) => handleEmergencyInfoChange(e, setFieldValue)}
                            onBlur={handleBlur}
                            data-index={index}
                            value={emergencyDetails.contactNo}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              })
            }

            <h1 className='form__section__title'>Referring Physician Information</h1>

            <div className='formRow formRow__4'>
              <div className="formCol">
                <div className="form__Field">
                  <label className='form__label' htmlFor='physicianName'>
                    Physician Name
                  </label>
                  <input
                    type="text"
                    id="physicianName"
                    name="physicianName"
                    placeholder="Physician Name"
                    onChange={handleChange}
                    className="form__input form__input--sm"
                    onBlur={handleBlur}
                    value={values.physicianName}
                  />
                  <ErrorMessage name="physicianName" component="div" className="error text-error" />
                </div>
              </div>
              <div className='formCol'>
                <div className="form__Field">
                  <label className='form__label' htmlFor='physicianContactNumber'>
                    Physician Contact Number
                  </label>
                  <div className='form__groupField form__groupField--phonenumber'>
                    <select id="physicianContactNumberCountryCode"
                      style={customStyle.select}
                      onChange={(e) => {
                        setFieldValue('physicianContactNumberCountryCode', e.target.value);
                        if (sameAsPhoneNo) {
                          setFieldValue('physicianWhatsappNumberCountryCode', e.target.value);
                        }
                      }}
                      className="form__select form__select--sm"
                      value={values.physicianContactNumberCountryCode}
                    >
                      {
                        props?.commonData?.countryCode.map((option) => (
                          <option
                            key={option}
                            value={option}
                          >
                            {option}
                          </option>
                        ))
                      }
                    </select>
                    <input
                      className="form__input form__input--sm"
                      type="text"
                      id="physicianContactNumber"
                      name="physicianContactNumber"
                      placeholder="Physician Contact Number"
                      pattern="[0-9]*"
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                        handleChange(e);
                      }}
                      onChange={handleChange}
                      maxLength={10}
                      onBlur={handleBlur}
                      value={values.physicianContactNumber}
                    />
                  </div>
                  <ErrorMessage name="physicianContactNumber" component="div" className="error text-error" />
                </div>
              </div>
              <div className="formCol">
                <div className='form__Field'>
                  <label className='form__label' htmlFor='physicianSpeciality' >Physician Speciality</label>
                  <select
                    id="physicianSpeciality"
                    name="physicianSpeciality"
                    className="form__select form__select--sm"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.physicianSpeciality}
                    style={customStyle.select}
                  >
                    <option value="">-Select-</option>
                    {props.commonData.physicianSpecialties.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className='formCol'>
                <div className="form__Field">
                  <label className='form__label' htmlFor='physicianWhatsappNumber'>
                    Physician Whatsapp Number
                  </label>
                  <div className='form__groupField form__groupField--phonenumber'>
                    <select id="physicianWhatsappNumberCountryCode"
                      style={customStyle.select}
                      onChange={(e) => {
                        setFieldValue('physicianWhatsappNumberCountryCode', e.target.value);
                      }}
                      className="form__select form__select--sm"
                      disabled={sameAsPhoneNo}
                      value={values.physicianWhatsappNumberCountryCode}
                    >
                      {
                        props?.commonData?.countryCode.map((option) => (
                          <option
                            key={option}
                            value={option}
                          >
                            {option}
                          </option>
                        ))
                      }
                    </select>
                    <input
                      className="form__input form__input--sm"
                      type="text"
                      id="physicianWhatsappNumber"
                      name="physicianWhatsappNumber"
                      placeholder="Physician Whatsapp Number"
                      onChange={handleChange}
                      pattern="[0-9]*"
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                        handleChange(e);
                      }}
                      disabled={sameAsPhoneNo}
                      maxLength={10}
                      onBlur={handleBlur}
                      value={sameAsPhoneNo ? values.physicianWhatsappNumber = values.physicianContactNumber : values.physicianWhatsappNumber}
                    />
                  </div>
                  <ErrorMessage name="physicianWhatsappNumber" component="div" className="error text-error" />
                  <div className='form__checkboxSubField'>
                    <div className='form__checkbox'>
                      <div className='form__checkbox__option m_0'>
                        <input id='sameAsContact' checked={sameAsPhoneNo}
                          className='form__checkbox__input'
                          type="checkbox"
                          name="sameAsContact" value="sameAsContact" onChange={(e) => {
                            setSameAsPhoneNo(!sameAsPhoneNo)
                            if (sameAsPhoneNo) {
                              values.physicianWhatsappNumber = ''
                              values.physicianWhatsappNumberCountryCode = "+91"
                            }
                          }} />
                        <label className='form__checkbox__label'>
                          Same as Contact Number
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='btn__group'>
              <button
                className='btn btn__white'
                onClick={() => props.setPageNumber((prev) => prev - 1)}
              >
                Previous
              </button>
              <button
                className='btn btn__primary'
                type="submit"
              >
                Next
              </button>
            </div>
          </ Form>
        )}}
      </Formik>
      <Loader isLoaderOpen={load} />
    </section>
  )
}

export default EmergencyContactandPhysician
